import type { InvalidRight } from '@/redux/slices/focusNewScheduleSlice';

export type GroupedRight = {
  territories: string[];
  rights: Array<{
    language: string;
    element_type: string;
    reason: string;
  }>;
};

const sortRights = (
  rights: Array<{
    language: string;
    element_type: string;
    reason: string;
  }>
) =>
  [...rights].sort(
    (a, b) => a.language.localeCompare(b.language) || a.element_type.localeCompare(b.element_type) || a.reason.localeCompare(b.reason)
  );

export const createRightsGroup = (rightsValidate: InvalidRight[]): GroupedRight[] => {
  const rightsByTerritory: Record<string, GroupedRight['rights']> = {};

  rightsValidate.forEach((right) => {
    const { territory, language, element_type, reason } = right;

    // Initialize territory key if it doesn't exist
    if (!rightsByTerritory[territory]) {
      rightsByTerritory[territory] = [];
    }

    // Add the right to the territory array
    rightsByTerritory[territory].push({
      language,
      element_type,
      reason
    });
  });

  const groupedRights: GroupedRight[] = [];
  const processedTerritories = new Set<string>();

  Object.entries(rightsByTerritory).forEach(([territory, rights]) => {
    if (processedTerritories.has(territory)) {
      return;
    }

    // Find all territories with identical rights arrays
    const matchingTerritories = [territory];
    const rightsString = JSON.stringify(sortRights(rights));

    Object.entries(rightsByTerritory).forEach(([otherTerritory, otherRights]) => {
      if (territory === otherTerritory || processedTerritories.has(otherTerritory)) {
        return;
      }

      const otherRightsString = JSON.stringify(sortRights(otherRights));

      if (rightsString === otherRightsString) {
        matchingTerritories.push(otherTerritory);
        processedTerritories.add(otherTerritory);
      }
    });

    processedTerritories.add(territory);

    groupedRights.push({
      territories: matchingTerritories.sort(),
      rights: sortRights(rights)
    });
  });

  return groupedRights;
};
